import { Sentry } from './index';

/**
 * 🤝 Sentry Spans
 *
 * https://docs.sentry.io/platforms/javascript/guides/react/migration/v7-to-v8/v8-new-performance-api/
 * https://docs.sentry.io/platforms/react-native/performance/instrumentation/custom-instrumentation/
 * https://blog.sentry.io/performance-monitoring-support-for-react-native/#transaction-hub
 *
 * This span hub is where all of the spans live to measure performance.
 * We can start and finish a span to send it off to Sentry.
 */
export const SentrySpanHub = {
  /**
   * Span operations we want to instrument
   *
   * Define the span operation. Give it a human-readable name and a unique name and op.
   * @example loginToHomescreen: { name: 'Login -> Homescreen', op: 'perf.userAction.loginToHomescreen' }
   */
  operations: {
    /**
     * Logging in to rendering the homescreen.
     */
    loginToHomescreen: { name: 'Login -> Homescreen', op: 'perf.userAction.loginToHomescreen' },

    /**
     * Pressing the add to cart button from the homescreen, going through all
     * the interstitial action sheets, and calling `handleAddToCart`.
     */
    addToCartFromHomescreen: { name: 'Add to Cart from Homescreen', op: 'perf.userAction.addToCart' },
  } as const,

  spans: {} as Record<string, Sentry.Span>,

  /**
   * Start the inactive span
   * @param spanContext Must include a human-readable `name` and `op`, which uniquely identifies the operation
   */
  startSpan(spanContext: { name: string; op: string }) {
    const span = Sentry.startInactiveSpan(spanContext);
    // eslint complains about span being undefined when linting apps/web so add guard to avoid false warning
    if (span) {
      this.spans[spanContext.op] = span;
    }
    return span;
  },

  /**
   * Ending the span will send it to Sentry
   * @param spanContext Must include a human-readable `name` and `op`, which uniquely identifies the operation
   */
  endSpan(spanContext: { name: string; op: string }) {
    const spanToEnd: Sentry.Span | undefined = this.spans[spanContext.op];
    if (!spanToEnd) return;

    spanToEnd.end(); // this will send the span to Sentry
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete this.spans[spanContext.op];
    return spanToEnd;
  },
};
